import { UndoManager, Array as YArray, YArrayEvent } from 'yjs';
import objectHash from 'object-hash';
import { EventMode, Graphics, PointData } from 'pixi.js';
import { Gfx } from './gfx';
import { Room } from '../sync/room';
import { ILayerSettings as ILayer, L, LayerDelta } from '../sync/_api';
import { Scribble } from '../util/scribble';
import { Util } from '../util/util';
import { KeyCode } from 'src/util/keycode';
import { Settings } from './settings';
import { Feature, GeoJsonProperties, MultiPolygon } from 'geojson';
import { booleanPointInPolygon, coordEach, difference, featureCollection, lineIntersect, lineSplit, lineString, multiPolygon, union } from '@turf/turf';
import { Geo } from 'src/util/line-overlap';

export interface ILayerConfig {
    name: string;
    alpha: number;
    events: EventMode;
    extents: 'polygon' | 'holes' | 'sprites';
    hotkey: KeyCode;
    selectable: boolean;
    drawing?: {
        fillColor: string;
        lineColor: string;
        lineWidth: number;
        lineAmplitude: number;
    };
}

export class Layer {
    public layer: Feature<MultiPolygon, GeoJsonProperties> = multiPolygon([]);
    public inverted: Feature<MultiPolygon, GeoJsonProperties> = multiPolygon([]);
    public drawTracking: { [id: string]: Graphics } = {};
    public undoStack?: UndoManager;
    public deltas!: YArray<LayerDelta>;
    public insideLines: { [key: string]: any; } = {};
    public insideLineGfx = Gfx.containers.layers[this.id].addChild(new Graphics());

    constructor(public id: L, public cfg: ILayerConfig, public data: ILayer) {
        Gfx.containers.layers[id].alpha = this.cfg.alpha;
        Gfx.containers.layers[id].eventMode = this.cfg.events;

        if (this.cfg.drawing) {
            this.deltas = Room.layerPolygons.get(id) ?? Room.layerPolygons.set(id, new YArray());
            this.undoStack = new UndoManager(this.deltas, { captureTimeout: 50, trackedOrigins: new Set([id]) });
            this.recalculate();
            this.deltas.observe((event) => {
                const lastDelta = this.getLastDelta(event);
                if (lastDelta) {
                    this.addDelta(lastDelta);
                } else {
                    this.recalculate();
                }
                this.draw();
            });
            this.update(data);
        } else if (id == L.Tokens) {
            this.undoStack = Room.tokens.createUndoManager(id);
        }
    }

    public update(data: ILayer) {
        this.data = data;
        this.data.fillColor = this.data.fillColor ?? this.cfg.drawing?.fillColor;
        this.data.lineColor = this.data.lineColor ?? this.cfg.drawing?.lineColor;
        this.data.lineWidth = this.data.lineWidth ?? this.cfg.drawing?.lineWidth;
        this.data.lineAmplitude = this.data.lineAmplitude ?? this.cfg.drawing?.lineAmplitude;
        this.draw();
    }

    private getLastDelta(event: YArrayEvent<LayerDelta>) {
        if (event.changes.delta.length != 2) return;
        const retained = event.changes.delta[0]['retain'];
        const current = event.target.length;
        if (retained == (current - 1)) {
            const insert = event.changes.delta[1]['insert'];
            return insert ? insert[0] as LayerDelta : undefined;
        }
        return;
    }

    private addWalls(polygon: Feature<MultiPolygon, GeoJsonProperties>) {
        const diff = difference(featureCollection([polygon, this.layer])) ?? multiPolygon([]);
        const overlap = Geo.lineOverlap(diff, this.layer);
        for (let item of overlap.features) {
            const path = item.geometry.coordinates.flat(2);
            for (let i = 0; i <= path.length - 4; i += 2) {
                const subpath = [path[i], path[i + 1], path[i + 2], path[i + 3]];
                this.insideLines[subpath.join()] = subpath;
            }
        }
    }

    private removeWalls(polygon: Feature<MultiPolygon, GeoJsonProperties>) {
        /* Check every existing line for removal conditions, and if a line has been split
            by the user edit we check each new segment individually */
        const keys = Object.keys(this.insideLines);
        for (let key of keys) {
            const linestring = lineString(Util.pathToPositions(this.insideLines[key]));
            const split = lineSplit(linestring, polygon);
            const isSplit = split.features.length > 0;
            const features = isSplit ? split.features : [linestring];
            for (let segment of features) {
                /* Remove all lines entirely inside the user edit, and not an edge of that edit. */
                const position0 = segment.geometry.coordinates[0];
                const position1 = segment.geometry.coordinates[1];
                const insides =
                    booleanPointInPolygon(position0, polygon, { ignoreBoundary: false }) &&
                    booleanPointInPolygon(position1, polygon, { ignoreBoundary: false });
                const notAnEdge = Geo.lineOverlap(segment, polygon).features.length == 0;
                if (insides && notAnEdge) {
                    delete this.insideLines[key];
                } else if (isSplit) {
                    const path = segment.geometry.coordinates.flat(2);
                    this.insideLines[path.join()] = path;
                }

                /* Removes excess edges created after a difference, where the edge is
                    left behind in a position now occupied by a standard polygon edge. */
                const outsideOverlap = Geo.lineOverlap(segment, this.layer);
                if (outsideOverlap.features.length > 0) {
                    delete this.insideLines[key];

                    /* Restore portions of the deleted line that were outside the altered geometry */
                    const first = segment.geometry.coordinates.flat(2);
                    const second = outsideOverlap.features[0].geometry.coordinates.flat(2);
                    const diff = Util.pathDifference(first, second);
                    if (diff.length > 0) {
                        this.insideLines[diff.join()] = diff;
                    }
                }
            }
        }
    }

    private addDelta(delta: LayerDelta) {
        try {
            const polygon = Util.pathToMultiPolygon(delta.p);
            if (delta.d) {
                this.addWalls(polygon);
                this.layer = union(featureCollection([this.layer, polygon])) as Feature<MultiPolygon, GeoJsonProperties> ?? multiPolygon([]);
                this.inverted = difference(featureCollection([Settings.worldPolygon, this.layer])) as Feature<MultiPolygon, GeoJsonProperties> ?? multiPolygon([]);
                this.removeWalls(polygon);
            } else {
                this.layer = difference(featureCollection([this.layer, polygon])) as Feature<MultiPolygon, GeoJsonProperties> ?? multiPolygon([]);
                this.inverted = difference(featureCollection([Settings.worldPolygon, this.layer])) as Feature<MultiPolygon, GeoJsonProperties> ?? multiPolygon([]);
                this.removeWalls(polygon);
            }
        } catch {
            this.layer = multiPolygon([]);
        }
    }

    public recalculate() {
        this.layer = multiPolygon([]);
        this.insideLines = {};
        for (let delta of this.deltas) {
            this.addDelta(delta);
        }
    }

    public reorderChildren() {
        const gfx = Gfx.containers.layers[this.id];
        gfx.children.sort((a, b) => a.zIndex - b.zIndex);
        for (let i = 0; i < gfx.children.length; i++) {
            gfx.children[i].zIndex = i;
        }
        gfx.sortChildren();
    }

    public addPath(path: number[], isAdd = true) {
        Room.document.transact(() => {
            this.deltas.insert(this.deltas.length, [{ d: isAdd, p: path }]);
        }, this.id);
    }

    public draw() {
        if (this.cfg.drawing == null) return;

        let polygons = this.layer == null ? [] : this.layer.geometry.type == 'MultiPolygon'
            ? this.layer.geometry.coordinates
            : [this.layer.geometry.coordinates];

        const shouldNotDraw = Object.keys(this.drawTracking);
        const allCurrentDraws = [];

        for (let polygon of polygons) {
            const hash = objectHash.sha1(polygon);
            allCurrentDraws.push(hash);
            if (shouldNotDraw.includes(hash)) { continue; }

            const polygonLines = this.getLines(polygon[0].flat(2));
            const polyGfx = this.getPolygonGraphics(polygonLines);
            let firstCut = true;
            if (polygon.length > 1) {
                for (let hole of polygon.slice(1)) {
                    const holeLines = this.getLines(hole.flat(2));
                    polyGfx.poly(holeLines).cut();
                    // NB: Bug in Pixi v8
                    if (firstCut) {
                        polyGfx.poly(holeLines).cut();
                        firstCut = false;
                    }
                }
            }
            Gfx.containers.layers[this.id].addChild(polyGfx);
            this.drawTracking[hash] = polyGfx;
        }

        for (let key of shouldNotDraw) {
            if (allCurrentDraws.indexOf(key) == -1) {
                this.drawTracking[key].destroy(true);
                delete this.drawTracking[key];
            }
        }

        Gfx.containers.layers[this.id].addChild(this.insideLineGfx.clear());
        const keys = Object.keys(this.insideLines);
        for (let key of keys) {
            const insideLine = this.getLines(this.insideLines[key]);
            this.drawLines(insideLine, this.insideLineGfx);
        }

        for (let id in this.drawTracking) {
            this.drawTracking[id].tint = this.data.fillColor;
        }
    }

    public getExtents() {
        let extents = [Infinity, Infinity, -Infinity, -Infinity];
        if (this.layer == null) return extents;

        let geojson = this.cfg.extents == 'holes'
            ? this.inverted
            : this.layer;

        coordEach(geojson as any, (coord) => {
            if (extents[0] > coord[0]) extents[0] = coord[0];
            if (extents[1] > coord[1]) extents[1] = coord[1];
            if (extents[2] < coord[0]) extents[2] = coord[0];
            if (extents[3] < coord[1]) extents[3] = coord[1];
        });
        return extents;
    }

    private drawLines(lines: PointData[], graphics: Graphics) {
        if (this.data.lineWidth > 0 && lines.length > 1) {
            graphics.moveTo(lines[0].x, lines[0].y);
            for (let line of lines.slice(1)) {
                graphics.lineTo(line.x, line.y);
            }
            graphics.stroke({ width: this.data.lineWidth, color: 'red', cap: 'round', join: 'round' })
        }
    }

    private getPolygonGraphics(lines: PointData[]) {
        const graphics = new Graphics();
        graphics.poly(lines).fill('0xFFFFFF').stroke({ width: this.data.lineWidth, color: this.data.lineColor, cap: 'round', join: 'round' });
        return graphics;
    }

    private getLines(path: number[]) {
        let polygonLines = Util.pathToPoints(path);
        return this.data.lineAmplitude == 0 ? polygonLines : Scribble.polygon(polygonLines);
    }
}